import {
  CBreadcrumbRouter,
  CHeader,
  CHeaderBrand,
  CHeaderNav,
  CHeaderNavItem,
  CImg,
  CToggler,
} from "@coreui/react";
import React, { useEffect, useRef } from "react";

import PropTypes from "prop-types";
import { TheHeaderDropdown } from "./index";
import { isFastDraftDeployment } from "src/reusable/Functions";
import routes from "../routes";
import { useNucAppStore } from "src/stores/NucAppStore";
import { ReleaseNotesButton } from "src/reusable/components/ReleaseNotes";
import { HelpButton } from "src/components/help";
import { GlobalSearchButton } from "src/features/globalSearch";
import { ConversationHistoryButton } from "src/features/conversationHistory";
import { NotificationsButton } from "src/features/notifications";
import DeveloperToolbar from "src/components/dev/DeveloperToolbar";

// CBreadcrumbRouter builds the trail by matching every cumulative URL segment
// against the route table. The Equipment AI route declares an array of paths
// (asset / asset+location / chat resume) under one name to stay mounted across
// navigation, so a nested URL like /asset/:id/location/:id matches the
// same-named route at multiple depths and the breadcrumb repeats "Equipment AI"
// once per segment. Collapse any array-path route to its base path for the
// breadcrumb only — page rendering still uses the full `routes` in TheContent.
// Pick the shortest (fewest-segment) path so the crumb matches regardless of
// how the array is ordered; Projects lists its paths most-specific-first, so
// path[0] would be the deepest and never match /assistant/projects.
const basePath = (paths) =>
  [...paths].sort((a, b) => a.split("/").length - b.split("/").length)[0];
export const breadcrumbRoutes = routes.map((route) =>
  Array.isArray(route.path) ? { ...route, path: basePath(route.path) } : route,
);

const TheHeader = ({ showSidebar, setShowSidebar, isLargeScreen }) => {
  const headerRef = useRef(null);
  const setHeaderHeight = useNucAppStore((state) => state.setHeaderHeight);

  const handleResize = () => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  };

  useEffect(() => {
    handleResize();

    window.addEventListener("resize", handleResize);

    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  return (
    <CHeader
      data-testid="hdr-container"
      innerRef={headerRef}
      style={{
        minHeight: "37px",
      }}
    >
      <CToggler
        data-testid="hdr-btn-toggle"
        data-attr="header:btn:toggle-sidebar"
        inHeader
        className="ml-md-3 d-lg-none"
        onClick={() => {
          if (!isLargeScreen()) {
            setShowSidebar(!showSidebar);
          }
        }}
        style={{
          minHeight: "30px",
        }}
      />
      <CHeaderBrand
        data-testid="hdr-link-home"
        data-attr="header:link:home-logo"
        className="mx-auto d-lg-none px-3"
        to="/"
        style={{
          minHeight: "30px",
        }}
      >
        <CImg
          src={
            isFastDraftDeployment()
              ? "/images/product-logos/Fast Draft Square 1656x220.png"
              : "/images/nuclearn-text-full.png"
          }
          height="25"
          alt="Logo"
        />
      </CHeaderBrand>

      <CBreadcrumbRouter
        data-testid="hdr-breadcrumb"
        className="border-0 c-subheader-nav m-0 px-6"
        routes={breadcrumbRoutes}
        style={{ paddingLeft: "15px", minHeight: "30px" }}
      />
      {/* Portal target for the route-gated "How to use X" pill, filled by
          GlobalHeaderHelpPill (mounted in TheLayout). Logic-free on purpose:
          the header owns only the slot's placement and vertical centering, not
          the pill or its route gating. */}
      <div
        id="hdr-pill-slot"
        className="d-flex align-items-center"
        style={{ marginLeft: "12px" }}
      />
      <CHeaderNav
        className="d-md-down-none mr-auto"
        style={{
          minHeight: "30px",
        }}
      >
        <CHeaderNavItem className="px-3"></CHeaderNavItem>
      </CHeaderNav>
      <CHeaderNav
        className="px-3"
        style={{
          minHeight: "30px",
        }}
      >
        <DeveloperToolbar />
        <GlobalSearchButton />
        <ConversationHistoryButton />
        <HelpButton />
        <NotificationsButton />
        <ReleaseNotesButton />
        <TheHeaderDropdown />
      </CHeaderNav>
    </CHeader>
  );
};

TheHeader.propTypes = {
  showSidebar: PropTypes.bool,
  setShowSidebar: PropTypes.func,
  isLargeScreen: PropTypes.func,
};

export default TheHeader;
